// feature/tipping-in-new-pledge | November 18th 2025: Adding tipping selector controller
import { Controller } from "stimulus"

// Tip frequency constants - must match enumerize definition in app/models/tip.rb
const TIP_FREQUENCY_ONCE = 'once'
const TIP_FREQUENCY_MONTHLY = 'monthly'

export default class extends Controller {
  static targets = [
    "tipType", "percentageSection", "amountSection", "tipPercentage",
    "tipsCents",
    "customPercentageInput", "customAmountInput", "tipDisplay",
    "percentageSlider", "amountSlider", "percentageSliderValue", "amountSliderValue",
    "tippingContent", "summaryRow", "noTipTab", "addTipTab", "giveOnceTab", "monthlyTab", "tipFrequency",
    "tippingBreakdown", "tipAmountDisplay", "tipFrequencyDisplay", "tipTypeDisplay", "tipTotalDisplay"
  ]

  connect() {
    // Default to "No tip": hide inputs and set values to zero
    this.selectNoTip(null)
    this._initializeSliderProgress()
  }

  selectNoTip(event) {
    this._preventDefault(event)
    this._setActiveTab('no')
    if (this.hasTippingContentTarget) {
      this.tippingContentTarget.classList.add('is-hidden')
    }
    if (this.hasTipsCentsTarget) this.tipsCentsTarget.value = 0
    if (this.hasTipPercentageTarget) this.tipPercentageTarget.value = 0
    this.updateTipDisplay(0)
    this._syncPercentageSlider(0)
    this._syncAmountSlider(0)
    
    // Hide the tipping breakdown when no tip is selected
    if (this.hasTippingBreakdownTarget) {
      this.tippingBreakdownTarget.classList.add('is-hidden')
    }
  }

  selectAddTip(event) {
    this._preventDefault(event)
    this._setActiveTab('yes')
    if (this.hasTippingContentTarget) {
      this.tippingContentTarget.classList.remove('is-hidden')
    }
    if (this.hasTipTypeTarget) {
      this.tipTypeTarget.value = 'percentage'
    }

    this._initializeToggleState()
    // Initialize with default tip of 5%
    const defaultPercentage = 5
    if (this.hasTipPercentageTarget) {
      this.tipPercentageTarget.value = defaultPercentage
    }
    this._syncPercentageSlider(defaultPercentage)
    this._setActiveButton(String(defaultPercentage), 'updatePercentage')
    // Calculate tip - this will update display and breakdown
    this.calculateTipFromPercentage(defaultPercentage)
    
    // Initialize tip frequency to "Monthly"
    this.selectMonthly()
    
    // Show the tipping breakdown when Add Tip is selected
    if (this.hasTippingBreakdownTarget) {
      this.tippingBreakdownTarget.classList.remove('is-hidden')
    }
  }

  _setActiveTab(mode) {
    const isNo = mode === 'no'
    if (this.hasNoTipTabTarget) {
      this.noTipTabTarget.classList.toggle('is-active', isNo)
    }
    if (this.hasAddTipTabTarget) {
      this.addTipTabTarget.classList.toggle('is-active', !isNo)
    }
  }

  selectGiveOnce() {
    this._setTipFrequency(TIP_FREQUENCY_ONCE)
  }

  selectMonthly() {
    this._setTipFrequency(TIP_FREQUENCY_MONTHLY)
  }

  _setTipFrequency(frequency) {
    if (this.hasTipFrequencyTarget) {
      this.tipFrequencyTarget.value = frequency
    }
    
    // Set active state for frequency tabs
    const isMonthly = frequency === TIP_FREQUENCY_MONTHLY
    if (this.hasMonthlyTabTarget) {
      this.monthlyTabTarget.classList.toggle('is-active', isMonthly)
    }
    if (this.hasGiveOnceTabTarget) {
      this.giveOnceTabTarget.classList.toggle('is-active', !isMonthly)
    }
    
    // Update the tipping breakdown when frequency changes
    const currentTipCents = this.hasTipsCentsTarget ? parseInt(this.tipsCentsTarget.value) : 0
    if (currentTipCents > 0) {
      this._updateTippingBreakdown(currentTipCents)
    }
    this.updateTipDisplay(currentTipCents)
  }

  // Toggle between percentage and amount modes
  toggleType(event) {
    this._preventDefault(event)
    // Get the tip type 
    const tipType = event?.currentTarget?.dataset?.radioSelectValue || event?.currentTarget?.dataset?.value || "percentage"
    
    // Update the tipType target value 
    if (this.hasTipTypeTarget) {
      this.tipTypeTarget.value = tipType
    }

    if (this.hasPercentageSectionTarget) {
      this.percentageSectionTarget.classList.toggle("is-hidden", tipType !== "percentage")
    }
    if (this.hasAmountSectionTarget) {
      this.amountSectionTarget.classList.toggle("is-hidden", tipType !== "amount")
    }
    
    // Set default values when switching modes
    if (tipType === "percentage") {
      if (this.hasTipPercentageTarget) {
        this.tipPercentageTarget.value = '5'
      }
      this._syncPercentageSlider(5)
      this.calculateTipFromPercentage(5)
      

      this._setActiveButton('5', 'updatePercentage')
    } else if (tipType === "amount") {
      // Set default to $5 when switching to amount mode
      const defaultAmountCents = 500 
      if (this.hasTipsCentsTarget) {
        this.tipsCentsTarget.value = defaultAmountCents
      }
      this._syncAmountSlider(5)
      this.updateTipDisplay(defaultAmountCents)
      this._setActiveButton('5', 'updateAmount')
    }
  }

  updatePercentage(event) {
    this._preventDefault(event)
    const percentage = parseFloat(event?.currentTarget?.dataset?.radioSelectValue || event?.currentTarget?.dataset?.value || this._eventValue(event)) || 0
    
    if (this.hasTipPercentageTarget) {
      this.tipPercentageTarget.value = percentage
    }
    this._syncPercentageSlider(percentage)
    this._clearCustomInput('customPercentageInput')
    
    // Update button visual selection (only if a real element triggered the event)
    if (event?.currentTarget?.classList) {
      this._setActiveButtonForEvent(event, 'updatePercentage')
    }
    
    // Calculate tip from the percentage
    this.calculateTipFromPercentage(percentage)
  }

  updateAmount(event) {
    this._preventDefault(event)
    const amount = parseFloat(event?.currentTarget?.dataset?.radioSelectValue || event?.currentTarget?.dataset?.value || this._eventValue(event)) || 0
    
    // For "Give Once", use the amount as-is. For "Monthly", the amount is already monthly.
    const tipAmountCents = Math.round(amount * 100)
    
    if (this.hasTipsCentsTarget) {
      this.tipsCentsTarget.value = tipAmountCents
    }
    this._syncAmountSlider(amount)
    
    // Clear custom input when preset button is selected
    this._clearCustomInput('customAmountInput')
    
    // Update button visual selection (only if a real element triggered the event)
    if (event?.currentTarget?.classList) {
      this._setActiveButtonForEvent(event, 'updateAmount')
    }
    
    this.updateTipDisplay(tipAmountCents)
    // Update breakdown if visible
    if (this.hasTippingBreakdownTarget && !this.tippingBreakdownTarget.classList.contains('is-hidden')) {
      this._updateTippingBreakdown(tipAmountCents)
    }
  }

  updateCustomPercentage() {
    if (!this.hasCustomPercentageInputTarget) return
    const percentage = parseFloat(this.customPercentageInputTarget.value) || 0
    if (this.hasTipPercentageTarget) {
      this.tipPercentageTarget.value = percentage
    }
    this.calculateTipFromPercentage(percentage)
  }

  updateCustomAmount() {
    if (!this.hasCustomAmountInputTarget) return
    const amount = parseFloat(this.customAmountInputTarget.value) || 0
    const tipAmountCents = Math.round(amount * 100)
    if (this.hasTipsCentsTarget) {
      this.tipsCentsTarget.value = tipAmountCents
    }
    this._syncAmountSlider(amount)
    this.updateTipDisplay(tipAmountCents)
    // Update breakdown if visible
    if (this.hasTippingBreakdownTarget && !this.tippingBreakdownTarget.classList.contains('is-hidden')) {
      this._updateTippingBreakdown(tipAmountCents)
    }
  }

  percentageSliderInput(event) {
    const percentage = parseFloat(event?.target?.value) || 0
    if (this.hasTipPercentageTarget) {
      this.tipPercentageTarget.value = percentage
    }
    this._syncPercentageSlider(percentage)

    this._clearButtonSelection('updatePercentage')
    this._clearCustomInput('customPercentageInput')

    this.calculateTipFromPercentage(percentage)
  }

  amountSliderInput(event) {
    const amount = parseFloat(event?.target?.value) || 0
    const tipAmountCents = Math.round(amount * 100)
    if (this.hasTipsCentsTarget) {
      this.tipsCentsTarget.value = tipAmountCents
    }
    this._syncAmountSlider(amount)
    this._clearButtonSelection('updateAmount')
    this._clearCustomInput('customAmountInput')
    this.updateTipDisplay(tipAmountCents)
    // Update breakdown if visible
    if (this.hasTippingBreakdownTarget && !this.tippingBreakdownTarget.classList.contains('is-hidden')) {
      this._updateTippingBreakdown(tipAmountCents)
    }
  }

  skipTip() {
    if (this.hasTipsCentsTarget) this.tipsCentsTarget.value = 0
    if (this.hasTipPercentageTarget) this.tipPercentageTarget.value = 0
    this.updateTipDisplay(0)
  }

  calculateTipFromPercentage(percentage) {
    const pledgeAmountCents = this._pledgeAmountCents()
    const pct = parseFloat(percentage) || 0
    
    let tipAmountCents = 0
    if (pledgeAmountCents > 0) {
      tipAmountCents = Math.round(pledgeAmountCents * (pct / 100))
      tipAmountCents = Math.max(0, tipAmountCents)
    }
    
    if (this.hasTipsCentsTarget) {
      this.tipsCentsTarget.value = tipAmountCents
    }
    this.updateTipDisplay(tipAmountCents)

    if (this.hasTippingBreakdownTarget && !this.tippingBreakdownTarget.classList.contains('is-hidden')) {
      this._updateTippingBreakdown(tipAmountCents)
    }
  }

  updateTipDisplay(tipAmountCents) {
    // Compute display string
    const currency = this._currencyCode()
    const symbol = this._currencySymbol(currency)
    const amount = (Math.max(0, tipAmountCents) / 100).toFixed(2)
    const frequency = this._getTipFrequency()
    const suffix = frequency === TIP_FREQUENCY_MONTHLY ? '/month' : ''
    const displayText = `${symbol}${amount}${suffix}`

    // Update UI 
    const tipDisplayEl = this.hasTipDisplayTarget 
      ? this.tipDisplayTarget 
      : document.querySelector('[data-tipping-selector-target="tipDisplay"]')
    if (tipDisplayEl) {
      tipDisplayEl.textContent = displayText
    }

    // Update summary row visibility
    if (tipAmountCents > 0) {
      const rowEl = this.hasSummaryRowTarget 
        ? this.summaryRowTarget 
        : document.querySelector('[data-tipping-selector-target="summaryRow"]')
      if (rowEl) rowEl.classList.remove('is-hidden')
    } else {
      this._updateSummaryVisibility(tipAmountCents)
    }
  }

  _initializeToggleState() {
    const tipType = this.hasTipTypeTarget ? this.tipTypeTarget.value : "percentage"
    
    if (this.hasPercentageSectionTarget) {
      this.percentageSectionTarget.classList.toggle("is-hidden", tipType !== "percentage")
    }
    if (this.hasAmountSectionTarget) {
      this.amountSectionTarget.classList.toggle("is-hidden", tipType !== "amount")
    }
    
    // Update the display format
    const currentTipCents = this.hasTipsCentsTarget ? parseInt(this.tipsCentsTarget.value) : 0
    this.updateTipDisplay(currentTipCents)
  }

  _initializeSliderProgress() {
    if (this.hasPercentageSliderTarget) {
      this._updateSliderProgress(this.percentageSliderTarget)
    }
    if (this.hasAmountSliderTarget) {
      this._updateSliderProgress(this.amountSliderTarget)
    }
  }

  _updateTippingBreakdown(tipAmountCents) {
    const symbol = this._currencySymbol(this._currencyCode())
    const amount = (Math.max(0, tipAmountCents) / 100).toFixed(2)
    const tipType = this.hasTipTypeTarget ? this.tipTypeTarget.value : 'percentage'
    const frequency = this._getTipFrequency()
    const isMonthly = frequency === TIP_FREQUENCY_MONTHLY
    
    // Update tip amount display
    if (this.hasTipAmountDisplayTarget) {
      this.tipAmountDisplayTarget.textContent = tipType === 'percentage'
        ? `${this.hasTipPercentageTarget ? this.tipPercentageTarget.value : '0'}% of pledge`
        : `${symbol}${amount}`
    }
    
    // Update frequency display
    if (this.hasTipFrequencyDisplayTarget) {
      this.tipFrequencyDisplayTarget.textContent = isMonthly ? 'Monthly' : 'One-time'
    }
    
    // Update tip type display
    if (this.hasTipTypeDisplayTarget) {
      this.tipTypeDisplayTarget.textContent = tipType === 'percentage' ? 'Percentage' : 'Fixed Amount'
    }
    
    // Update total display
    if (this.hasTipTotalDisplayTarget) {
      this.tipTotalDisplayTarget.textContent = isMonthly 
        ? `${symbol}${amount}/month`
        : `${symbol}${amount} (one-time)`
    }
  }

  _updateSummaryVisibility(tipAmountCents) {
      if (!this.hasSummaryRowTarget) return
      const addTipActive = this.hasAddTipTabTarget && this.addTipTabTarget.classList.contains('is-active')
      const hide = !addTipActive || (parseInt(tipAmountCents) <= 0)
      this.summaryRowTarget.classList.toggle('is-hidden', hide)
  }

  customPercentageInput(event) {
    const value = parseInt(event.target.value)
    if (isNaN(value) || value <= 0 || value > 100) {
      event.target.setCustomValidity('Please enter a percentage between 0 and 100')
      return
    }
    event.target.setCustomValidity('')
    
    // Clear radio button selections when using custom input
    this._clearButtonSelection('updatePercentage')
    this._syncPercentageSlider(value)
    
    // Update the percentage value and calculate tip
    if (this.hasTipPercentageTarget) {
      this.tipPercentageTarget.value = value.toString()
    }
    this.calculateTipFromPercentage(value)
  }

  customAmountInput(event) {
    const value = parseFloat(event.target.value)
    if (isNaN(value) || value <= 0) {
      event.target.setCustomValidity('Please enter a valid amount greater than 0')
      return
    }
    
    event.target.setCustomValidity('')
    
    // Clear radio button selections when using custom input
    this._clearButtonSelection('updateAmount')
    this._syncAmountSlider(value)
    
    // Convert to cents and update
    const amountCents = Math.round(value * 100)
    if (this.hasTipsCentsTarget) {
      this.tipsCentsTarget.value = amountCents
    }
    this.updateTipDisplay(amountCents)
  }

  _eventValue(event) {
    return event?.params?.value || event?.currentTarget?.dataset?.value || event?.target?.value || event?.currentTarget?.value
  }

  _preventDefault(event) {
    if (event?.preventDefault) {
      event.preventDefault()
    }
  }

  _getTipFrequency() {
    return this.hasTipFrequencyTarget ? (this.tipFrequencyTarget.value || TIP_FREQUENCY_ONCE) : TIP_FREQUENCY_ONCE
  }

  _setActiveButton(value, actionType) {
    const selector = `[data-radio-select-value="${value}"][data-action*="tipping-selector#${actionType}"]`
    const button = document.querySelector(selector)
    if (button) {
      this._clearButtonSelection(actionType)
      button.classList.add('is-active')
    }
  }

  _setActiveButtonForEvent(event, actionType) {
    this._clearButtonSelection(actionType)
    event.currentTarget.classList.add('is-active')
  }

  _clearButtonSelection(actionType) {
    const buttons = document.querySelectorAll(`[data-action*="${actionType}"]`)
    buttons.forEach(btn => btn.classList.remove('is-active'))
  }

  _clearCustomInput(targetName) {
    let input
    if (targetName === 'customPercentageInput' && this.hasCustomPercentageInputTarget) {
      input = this.customPercentageInputTarget
    } else if (targetName === 'customAmountInput' && this.hasCustomAmountInputTarget) {
      input = this.customAmountInputTarget
    } else {
      input = document.querySelector(`[data-tipping-selector-target="${targetName}"]`)
    }
    if (input) input.value = ''
  }

  _syncPercentageSlider(value) {
    if (this.hasPercentageSliderTarget) {
      this.percentageSliderTarget.value = value
      this._updateSliderProgress(this.percentageSliderTarget)
    }
    if (this.hasPercentageSliderValueTarget) {
      this.percentageSliderValueTarget.textContent = `${this._formatPercentage(value)}%`
    }
  }

  _syncAmountSlider(value) {
    if (this.hasAmountSliderTarget) {
      this.amountSliderTarget.value = value
      this._updateSliderProgress(this.amountSliderTarget)
    }
    if (this.hasAmountSliderValueTarget) {
      this.amountSliderValueTarget.textContent = this._formatMoney(value)
    }
  }

  _updateSliderProgress(sliderEl) {
    if (!sliderEl) return
    const min = parseFloat(sliderEl.min || 0)
    const max = parseFloat(sliderEl.max || 100)
    const value = parseFloat(sliderEl.value || 0)
    const percent = max === min ? 0 : ((value - min) / (max - min)) * 100
    sliderEl.style.setProperty('--slider-progress', `${percent}%`)
    const container = sliderEl.closest('.slider-container')
    if (container) {
      container.style.setProperty('--slider-progress', `${percent}%`)
    }
    sliderEl.style.background = [
      `linear-gradient(90deg, #2675F8 0% ${percent}%, rgba(38,117,248,0) ${percent}% 100%)`,
      `repeating-linear-gradient(90deg, transparent 0 calc(10% - 2px), rgba(130,138,150,0.9) calc(10% - 2px) 10%)`,
      '#E4E7EC'
    ].join(', ')
    sliderEl.style.backgroundRepeat = 'no-repeat, repeat, no-repeat'
    sliderEl.style.backgroundSize = '100% 100%, 100% 100%, 100% 100%'
  }

  _formatPercentage(value) {
    const numeric = parseFloat(value) || 0
    return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1)
  }

  _formatMoney(value) {
    const numeric = parseFloat(value) || 0
    const symbol = this._currencySymbol(this._currencyCode())
    return `${symbol}${Number.isInteger(numeric) ? numeric : numeric.toFixed(2)}`
  }

  _pledgeAmountCents() {
    const amountCentsField = document.querySelector('[data-oftw-wizard-target="amountCents"]')
    if (!amountCentsField) return null
    const value = parseInt(amountCentsField.value)
    return isNaN(value) ? null : value
  }

  _annualIncomeCents() {
    // Try multiple selectors to find the income field
    let incomeField = document.querySelector('[data-oftw-wizard-target="estimatedFutureIncome"]')
    if (!incomeField) {
      incomeField = document.querySelector('input[name*="estimated_future_annual_income"]')
    }
    if (!incomeField) {
      incomeField = document.querySelector('input[data-oftw-wizard-target*="estimatedFutureIncome"]')
    }
    
    if (!incomeField) return null
    const value = parseInt(incomeField.value)
    return isNaN(value) ? null : value * 100 // Convert to cents
  }

  // fix/tipping-ui | January 7th 2026: Get currency code from wizard controller
  _currencyCode() {
    
    const formEl = document.querySelector('[data-controller*="oftw-wizard"]')
    if (formEl) {
      const wizardController = this.application.getControllerForElementAndIdentifier(formEl, 'oftw-wizard')
      if (wizardController && wizardController.currencyCodeValue) {
        return wizardController.currencyCodeValue
      }
    }
    return 'USD'
  }

  // fix/tipping-ui | January 7th 2026: Get currency symbol from wizard controller
  _currencySymbol(code) {
    const formEl = document.querySelector('[data-controller*="oftw-wizard"]')
    if (formEl) {
      const wizardController = this.application.getControllerForElementAndIdentifier(formEl, 'oftw-wizard')
      if (wizardController && wizardController.currencySymbol) {
        return wizardController.currencySymbol
      }
    }
    return '$'
  }
}